import { print } from 'graphql'
import KnowError from '~/exceptions/KnownError'
import { gql, type ResultOf, type VariablesOf } from './financial-bff-gql'
import http from './financial-bff.http'

export const WALLET_PAYMENT_MODEL = gql(`
  query WalletPaymentModel($partnerId: String) {
    wallet_payment_model(partner_id: $partnerId) {
      summary {
        state
        current_model
        upfront_percentage
        after_completion_percentage
        performance_percentage
        disabled_model
        disabled_reason
        novice_completed
        total_completed
        next_model
        projects_needed_for_next_model
        is_blocked
        block_reason
        updated_at
      }
      models {
        key
        upfront_percentage
        after_completion_percentage
        min_on_time_percentage
        state
        requirements {
          code
          is_met
          is_waived
        }
      }
      default_model_conditions {
        code
        value
      }
      performance {
        total_completed
        on_time_completed
        late_completed
        on_time_percentage
        thresholds {
          model_b
          model_a
        }
        challenge {
          target_model
          projects_needed
        }
        by_kit_type {
          kit_type
          on_time_count
          late_count
        }
      }
      eligibility_context {
        partner_profile
        is_novice
        novice_completed
        novice_required
        performance_model
        delinquency_status
        commercial_exception {
          is_active
        }
      }
    }
  }
`)

export type WalletPaymentModelVariables = VariablesOf<typeof WALLET_PAYMENT_MODEL>
export type WalletPaymentModel = NonNullable<ResultOf<typeof WALLET_PAYMENT_MODEL>['wallet_payment_model']>

export const WALLET_FORBIDDEN_ERROR_CODE = 'FORBIDDEN'

export const WALLET_DETERMINISTIC_ERROR_CODES: ReadonlySet<string> = new Set([
  WALLET_FORBIDDEN_ERROR_CODE,
  'UNAUTHORIZED',
  'NOT_FOUND',
  'BAD_REQUEST',
  'UNPROCESSABLE_ENTITY',
])

export function isWalletForbiddenError(error: unknown) {
  return error instanceof Error && error.message === WALLET_FORBIDDEN_ERROR_CODE
}

export function isWalletDeterministicError(error: unknown) {
  return error instanceof Error && WALLET_DETERMINISTIC_ERROR_CODES.has(error.message)
}

export const WALLET_INVALID_RESPONSE_ERROR_CODE = 'INVALID_RESPONSE'

interface WalletPaymentModelGraphqlBody {
  data?: ResultOf<typeof WALLET_PAYMENT_MODEL> | null
  errors?: { message: string }[] | null
}

function isGraphqlBody(body: unknown): body is WalletPaymentModelGraphqlBody {
  return typeof body === 'object' && body !== null && !Array.isArray(body)
}

export async function fetchWalletPaymentModel(variables: WalletPaymentModelVariables) {
  const { data: body } = await http.post<unknown>('', { query: print(WALLET_PAYMENT_MODEL), variables })
  if (!isGraphqlBody(body))
    throw new KnowError(WALLET_INVALID_RESPONSE_ERROR_CODE)

  const paymentModel = body.data?.wallet_payment_model
  const graphqlErrors = Array.isArray(body.errors) ? body.errors : []

  if (paymentModel)
    return paymentModel
  if (graphqlErrors.length > 0)
    throw new KnowError(graphqlErrors[0].message)
  if (body.data && paymentModel === null)
    return null
  throw new KnowError(WALLET_INVALID_RESPONSE_ERROR_CODE)
}
